import { type ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, type IconComponent, iconRegistry } from "@/icons";
import type { StreamVisibility } from "../spec/stream-visibility";
import { useGetDisplayInfo } from "../units/use-get-display-info";
import type { PickerGroupLabels } from "./group-picker-streams";
import type { PickerRowText } from "./picker-row-text";
import type { PickerStream } from "./picker-stream";

export function usePickerLabels(): PickerGroupLabels {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const thisZone = t("this-zone");
    const noZone = t("no-zone");
    return useMemo(() => ({ thisZone, noZone }), [thisZone, noZone]);
}

export const ROW_CLASS = "flex min-h-8 w-full items-center gap-2 py-1 pr-2 pl-3.5 text-left";

export function StatusLine({ children }: { children: string }) {
    return <p className={"px-3.5 pt-8 pb-2 text-center text-muted-foreground text-sm"}>{children}</p>;
}

export function GroupHeader({ label, count, className = "" }: { label: string; count: number; className?: string }) {
    return (
        <div className={`flex min-h-[26px] items-center gap-1.5 border-y bg-muted px-3.5 py-[3px] ${className}`}>
            <span className={"truncate font-semibold text-xs"}>{label}</span>
            <span className={"ml-auto text-muted-foreground text-xs tabular-nums"}>{count}</span>
        </div>
    );
}

export function GroupSection({
    label,
    count,
    belowBorder = false,
    children,
}: {
    label: string;
    count: number;
    belowBorder?: boolean;
    children: ReactNode;
}) {
    // Under a border, the header's top border sits 1px above the scroll edge, which clips it.
    const edge = belowBorder ? "first:-mt-px" : "first:mt-0";
    const stick = belowBorder ? "-top-px" : "top-0";
    return (
        <div role={"group"} aria-label={label} className={`mt-1.5 flex flex-col ${edge}`}>
            <GroupHeader label={label} count={count} className={`sticky ${stick} z-[1]`} />
            <div className={"flex flex-col pt-1"}>{children}</div>
        </div>
    );
}

export const VISIBILITY_DISPLAY = {
    shown: { Icon: null, labelKey: null, filterKey: "filter-field-data" },
    diagnostic: {
        Icon: iconRegistry[ICON_KEYS.DIAGNOSTIC],
        labelKey: "diagnostic-label",
        filterKey: "filter-diagnostic",
    },
    adminOnly: {
        Icon: iconRegistry[ICON_KEYS.ADMIN_ONLY],
        labelKey: "admin-only-label",
        filterKey: "filter-admin-only",
    },
} as const satisfies Record<
    StreamVisibility,
    { Icon: IconComponent | null; labelKey: string | null; filterKey: string }
>;

/** A row's button label. The icon and the zone header can't be read out, so the label carries both. */
export function useRowLabel() {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    return useCallback(
        (action: "add" | "remove", stream: PickerStream, text: PickerRowText, group?: string) => {
            const { labelKey } = VISIBILITY_DISPLAY[stream.visibility];
            const name = labelKey
                ? t("name-with-level", { name: text.measurement, level: t(labelKey) })
                : text.measurement;
            let detail = text.detail;
            if (group) detail = detail ? t("detail-in-group", { detail, group }) : group;
            if (action === "add")
                return detail ? t("add-row", { name, detail }) : t("add-row-without-detail", { name });
            return detail ? t("remove-row", { name, detail }) : t("remove-row-without-detail", { name });
        },
        [t],
    );
}

/** Holds its width on every row, so names stay lined up whether or not a row has an icon. */
export function VisibilityMark({ visibility }: { visibility: StreamVisibility }) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const { Icon, labelKey } = VISIBILITY_DISPLAY[visibility];
    return (
        <span
            className={"flex size-3.5 shrink-0 items-center justify-center text-muted-foreground"}
            title={labelKey ? t(labelKey) : undefined}
        >
            {Icon && <Icon aria-hidden className={"size-3.5"} />}
        </span>
    );
}

export function RowText({
    stream,
    text: { measurement, detail },
    showVisibility = false,
}: {
    stream: PickerStream;
    text: PickerRowText;
    /** Only while the list holds diagnostics, so the default list keeps its full width for names. */
    showVisibility?: boolean;
}) {
    const getDisplayInfo = useGetDisplayInfo();
    const unitSymbol = getDisplayInfo(stream.unitCategory).symbol;
    return (
        <span className={"flex min-w-0 flex-1 items-baseline gap-3"}>
            <span className={"flex min-w-0 items-baseline gap-1.5"}>
                {showVisibility && (
                    <span className={"self-center"}>
                        <VisibilityMark visibility={stream.visibility} />
                    </span>
                )}
                <span className={"truncate text-[13px] leading-5"}>{measurement}</span>
                {unitSymbol && (
                    <span className={"shrink-0 text-[10px] text-muted-foreground leading-4"}>{unitSymbol}</span>
                )}
            </span>
            {detail && (
                <span className={"min-w-16 flex-1 truncate text-right text-muted-foreground/72 text-xs leading-4"}>
                    {detail}
                </span>
            )}
        </span>
    );
}
